import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const MultiLanguage = makeShortcode("MultiLanguage");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Groups are created and defined in your code when sending events. `}</p>
    <p>{`In the example below, we create a group type `}<inlineCode parentName="p">{`company`}</inlineCode>{`. Then, for each company, we set the `}<inlineCode parentName="p">{`group key`}</inlineCode>{` as the unique identifier for that specific company. This can be anything that helps you identify it, such as ID or domain.`}</p>
    <blockquote>
      <p parentName="blockquote">{`We advise against using the `}<em parentName="p">{`name`}</em>{` of the company (or any other group) as the key, because that's rarely guaranteed to be unique, and thus can affect the quality of analytics data.`}</p>
    </blockquote>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-js"
        }}>{`// All events for that session will be associated with company \`company_id_on_your_db\`
posthog.group('company', 'company_id_in_your_db');

posthog.capture('user_signed_up')
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`posthog.capture('[distinct id]', 'user_signed_up', groups={'company': 'company_id_in_your_db'})
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`client.Enqueue(posthog.Capture{
    DistinctId: "[distinct id]",
    Event: "user_signed_up",
    Groups: posthog.NewGroups().
        Set("company", "company_id_in_your_db").
})
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`posthog.capture({
    event: 'user_signed_up',
    distinctId: '[distinct id]',
    groups: { company: 'company_id_in_your_db' }
})
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-php"
        }}>{`PostHog::capture(array(
    'distinctId' => '[distinct id]',
    'event' => 'user_signed_up',
    '$groups' => array("company" => "company_id_in_your_db")
));
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-segment"
        }}>{`// You'll always need to pass through the $groups object for Segment, even for analytics.js
analytics.track('user_signed_up', {
    $groups: { segment_group: 'company_id_in_your_db' }
})

\`\`\`bash
curl -v -L --header "Content-Type: application/json" -d '{
    "api_key": "<ph_project_api_key>",
    "event": "user_signed_up",
    "distinct_id": "[distinct id]",
    "properties": {
        "$groups": {"company": "company_id_in_your_db"}
    }
}' <ph_instance_address>/capture/
`}</code></pre>
    </MultiLanguage>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Tip:`}</strong>{` When specifying the group type, use the singular version for clarity (`}<inlineCode parentName="p">{`company`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`companies`}</inlineCode>{`).`}</p>
    </blockquote>
    <p>{`We now have one `}<inlineCode parentName="p">{`company`}</inlineCode>{`-type group with a key `}<inlineCode parentName="p">{`company_id_in_your_db`}</inlineCode>{`. When we send the event `}<inlineCode parentName="p">{`user_signed_up`}</inlineCode>{`, it will be attached to this newly created group.`}</p>
    <p>{`A single event can only belong to a single group per group type. To elaborate what we mean, we'll continue using the example above:`}</p>
    <ul>
      <li parentName="ul">{`It is `}<strong parentName="li">{`NOT`}</strong>{` possible to assign the a single `}<inlineCode parentName="li">{`user_signed_up`}</inlineCode>{` event to two companies at the same time.`}</li>
      <li parentName="ul">{`It is possible to assign this event to a different group type, for example `}<inlineCode parentName="li">{`channel`}</inlineCode>{` or `}<inlineCode parentName="li">{`post`}</inlineCode>{`, in addition to the `}<inlineCode parentName="li">{`company`}</inlineCode>{` group type.`}</li>
    </ul>
    <MultiLanguage mdxType="MultiLanguage">
      <pre><code parentName="pre" {...{
          "className": "language-js"
        }}>{`// ❌ Not possible
posthog.group('company', 'company_id_in_your_db');
posthog.group('company', 'another_company_id_in_your_db');
posthog.capture('user_signed_up')

// ✅ Allowed
posthog.group('company', 'company_id_in_your_db');
posthog.group('channel', 'channel_id_in_your_db');
posthog.capture('user_signed_up')

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# ❌ Not possible
posthog.capture('[distinct id]', 'user_signed_up', groups={'company': 'company_id_in_your_db', 'company': 'another_company_id_in_your_db'})

# ✅ Allowed
posthog.capture('[distinct id]', 'user_signed_up', groups={'company': 'company_id_in_your_db', 'channel': 'channel_id_in_your_db'})
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`// ❌ Not possible
client.Enqueue(posthog.Capture{
    DistinctId: "[distinct id]",
    Event: "user_signed_up",
    Groups: posthog.NewGroups().
        Set("company", "company_id_in_your_db").
        Set("company", "another_company_id_in_your_db").
})

// ✅ Allowed
client.Enqueue(posthog.Capture{
    DistinctId: "[distinct id]",
    Event: "user_signed_up",
    Groups: posthog.NewGroups().
        Set("company", "company_id_in_your_db").
        Set("channel", "channel_id_in_your_db").
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// ✅ Allowed
posthog.capture({
    event: 'user_signed_up',
    distinctId: '[distinct id]',
    groups: { 
        company: 'company_id_in_your_db',
        channel: 'channel_id_in_your_db'
    }
})
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-php"
        }}>{`// ❌ Not possible
PostHog::capture(array(
    'distinctId' => '[distinct id]',
    'event' => 'user_signed_up',
    '$groups' => array("company" => "company_id_in_your_db", "company" => "another_company_id_in_your_db")
));
// ✅ Allowed
PostHog::capture(array(
    'distinctId' => '[distinct id]',
    'event' => 'user_signed_up',
    '$groups' => array("company" => "company_id_in_your_db", "channel" => "channel_id_in_your_db")
));
`}</code></pre>
    </MultiLanguage>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Group type limit:`}</strong>{` There's a hard limit of 5 group types within PostHog, although within each group type you can have an unlimited number of groups.`}</p>
    </blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      